import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Send, CheckCircle2, XCircle, Clock, KeyRound } from "lucide-react";
import { toast } from "@/components/Toaster";

const STATUS_META = {
  pending:  { label: "Pending review", icon: Clock,        cls: "text-amber-500 border-amber-500/40 bg-amber-500/[0.08]" },
  approved: { label: "Approved",       icon: CheckCircle2, cls: "text-emerald-500 border-emerald-500/40 bg-emerald-500/[0.08]" },
  denied:   { label: "Declined",       icon: XCircle,      cls: "text-rose-500 border-rose-500/40 bg-rose-500/[0.08]" },
};

export default function RequestAccess() {
  const [roomNameHint, setRoomNameHint] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/access-requests/mine");
      setHistory(data);
    } catch (e) {
      // Listing failures aren't fatal — user can still submit a new request.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setErr("Please describe what you need access to."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/access-requests", {
        room_name_hint: roomNameHint.trim() || null,
        reason: reason.trim(),
      });
      toast.success("Request sent. An Owner will review it shortly.");
      setRoomNameHint(""); setReason("");
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="label-mono text-muted-foreground flex items-center gap-2">
        <KeyRound className="w-3 h-3" /> Request access
      </div>
      <h1 className="mt-3 font-display text-4xl tracking-tighter font-medium">Need into a data room?</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Tell an App Owner what you need to see and why. They&apos;ll review your request and either grant
        you access with the right permissions or get in touch with questions.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4 border border-border bg-card rounded-md p-6">
        <div>
          <label className="label-mono">Data room (optional)</label>
          <input
            data-testid="request-room-hint"
            value={roomNameHint} onChange={(e) => setRoomNameHint(e.target.value)}
            placeholder="e.g. BlackPeak Capital — SovereignHPC acquisition"
            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">Leave blank if you&apos;re not sure which room — the Owner will pick.</p>
        </div>
        <div>
          <label className="label-mono">Reason</label>
          <textarea
            data-testid="request-reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="What do you need access to and why? Owners use this to assign the right permissions."
            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            required
          />
        </div>
        {err && <div className="text-sm text-destructive">{err}</div>}
        <div className="flex justify-end">
          <button
            type="submit" disabled={busy}
            data-testid="request-submit"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </form>

      {/* History */}
      <div className="mt-8">
        <h2 className="font-display text-xl tracking-tight">Your past requests</h2>
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : history.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">
              No requests yet. Once you send one, you&apos;ll see its status here.
            </div>
          ) : history.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={r.id} data-testid={`my-request-${r.id}`} className="border border-border rounded-md p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <span className="label-mono text-muted-foreground">Room: </span>
                      {r.room_name ? <span className="text-emerald-500">{r.room_name}</span>
                        : r.room_name_hint ? <span className="text-muted-foreground italic">{r.room_name_hint}</span>
                        : <span className="text-muted-foreground italic">unspecified</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Requested {new Date(r.requested_at).toLocaleString()}
                      {r.decided_at && <> · Decided {new Date(r.decided_at).toLocaleString()}</>}
                    </div>
                    <div className="mt-2 text-sm whitespace-pre-wrap bg-secondary/30 border border-border rounded-md p-2">
                      {r.reason}
                    </div>
                    {r.deny_note && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <span className="label-mono">Owner note: </span>{r.deny_note}
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex items-center gap-1.5 mono uppercase tracking-wider text-[11px] px-2 py-1 rounded border ${meta.cls}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
